import { Box, HStack, Input, Stack, Text } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { useRef } from "react"
import { FiTrash2, FiUpload } from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

export interface AvatarUploadProps {
  currentAvatarUrl?: string | null
  onUploadSuccess?: (avatarUrl: string) => void
  onDeleteSuccess?: () => void
}

const MAX_FILE_SIZE_MB = 5
const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]

export const AvatarUpload = ({
  currentAvatarUrl,
  onUploadSuccess,
  onDeleteSuccess,
}: AvatarUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await api.users.uploadAvatarApiV1UsersMeAvatarPost({
        formData: { file },
      })
      return response.data ?? {}
    },
    onSuccess: (data) => {
      showSuccessToast("Avatar updated successfully")
      if (data && typeof data === "object" && "avatar_url" in data) {
        onUploadSuccess?.((data as { avatar_url: string }).avatar_url)
      } else if (currentAvatarUrl) {
        onUploadSuccess?.(currentAvatarUrl)
      }
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to upload avatar: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.users.deleteAvatarApiV1UsersMeAvatarDelete()
    },
    onSuccess: () => {
      showSuccessToast("Avatar removed successfully")
      onDeleteSuccess?.()
    },
    onError: (error: Error) => {
      showErrorToast(
        `Failed to remove avatar: ${error?.message || "Unknown error"}`,
      )
    },
  })

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      return `File type "${file.type}" is not supported. Please use JPEG, PNG, GIF, or WebP.`
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`
    }
    return null
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]
    const error = validateFile(file)
    if (error) {
      showErrorToast(error)
      return
    }

    uploadMutation.mutate(file)

    // Reset input value to allow selecting the same file again
    e.target.value = ""
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleDeleteClick = () => {
    if (confirm("Are you sure you want to remove your avatar?")) {
      deleteMutation.mutate()
    }
  }

  return (
    <Box>
      <HStack justify="center" gap={3}>
        <Button
          size="sm"
          variant="outline"
          onClick={handleUploadClick}
          loading={uploadMutation.isPending}
          disabled={deleteMutation.isPending}
          gap={1}
        >
          <FiUpload />
          {currentAvatarUrl ? "Change Avatar" : "Upload Avatar"}
        </Button>

        {currentAvatarUrl && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={handleDeleteClick}
            loading={deleteMutation.isPending}
            disabled={uploadMutation.isPending}
            gap={1}
          >
            <FiTrash2 />
            Remove
          </Button>
        )}
      </HStack>

      <Stack align="center" gap={1} mt={2}>
        <Text fontSize="xs" color="gray.500" textAlign="center">
          Maximum file size: {MAX_FILE_SIZE_MB}MB
        </Text>
        <Text fontSize="xs" color="gray.500" textAlign="center">
          Supported formats: JPEG, PNG, GIF, WebP
        </Text>
      </Stack>

      <Input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(",")}
        onChange={handleFileSelect}
        display="none"
      />
    </Box>
  )
}

export default AvatarUpload
